/**
 * useTimecodeDisplay Hook
 * 
 * 管理時間碼顯示功能，包括：
 * - 時間碼顯示狀態
 * - 時間碼位置設定
 * - 時間碼背景設定
 * - 時間碼自動更新
 * 
 * @author AI Assistant
 * @date 2025-11-02
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// 時間碼位置類型
export type TimecodePosition = 'topLeft' | 'topCenter' | 'topRight' | 'bottomLeft' | 'bottomCenter' | 'bottomRight';

// Hook 返回類型
export interface UseTimecodeDisplayReturn {
  // 狀態
  currentTimecode: string;
  showTimecode: boolean;
  showTimecodeBackground: boolean;
  timecodePosition: TimecodePosition;
  timecodeScale: number; // 🔧 新增：時間碼縮放比例

  // 操作函數
  setCurrentTimecode: (timecode: string) => void;
  setShowTimecode: (show: boolean) => void;
  setShowTimecodeBackground: (show: boolean) => void;
  setTimecodePosition: (position: TimecodePosition) => void;
  setTimecodeScale: (scale: number) => void; // 🔧 新增：設置縮放比例

  // 輔助函數
  updateTimecode: (time: number, frameRate?: number) => void;
  resetTimecode: () => void;
  
  // Refs（用於性能優化）
  timecodeIntervalRef: React.RefObject<NodeJS.Timeout | null>;
  timecodeRafIdRef: React.RefObject<number | null>;
  timecodeElRef: React.RefObject<HTMLElement | null>;
  lastKnownTimeRef: React.RefObject<number>;
  lastTickRef: React.RefObject<number>;
}

/**
 * 格式化時間為時間碼字符串
 */
const formatTime = (seconds: number, includeFrames: boolean = true, frameRate: number = 30): string => {
  const pad = (num: number): string => num.toString().padStart(2, '0');
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (includeFrames) {
    const frames = Math.floor((seconds % 1) * frameRate);
    return hours > 0 
      ? `${hours}:${pad(minutes)}:${pad(secs)} ${pad(frames)}`
      : `${minutes}:${pad(secs)} ${pad(frames)}`;
  } else {
    return hours > 0 
      ? `${hours}:${pad(minutes)}:${pad(secs)}`
      : `${minutes}:${pad(secs)}`;
  }
};

/**
 * 時間碼顯示 Hook
 * 
 * @returns {UseTimecodeDisplayReturn} 時間碼顯示狀態和操作函數
 * 
 * @example
 * ```typescript
 * const {
 *   currentTimecode,
 *   showTimecode,
 *   setShowTimecode,
 *   updateTimecode
 * } = useTimecodeDisplay();
 * 
 * // 更新時間碼
 * updateTimecode(currentTime, 30); // 30 fps
 * ```
 */
export const useTimecodeDisplay = (): UseTimecodeDisplayReturn => {
  // ========== 時間碼狀態 ==========
  const [currentTimecode, setCurrentTimecode] = useState<string>('00:00:00 00');
  const [showTimecode, setShowTimecode] = useState<boolean>(() => {
    const saved = localStorage.getItem('showTimecode');
    return saved !== 'false'; // 預設顯示
  });
  const [showTimecodeBackground, setShowTimecodeBackground] = useState<boolean>(() => {
    const saved = localStorage.getItem('showTimecodeBackground');
    return saved !== 'false'; // 預設顯示
  });
  const [timecodePosition, setTimecodePosition] = useState<TimecodePosition>(() => {
    const saved = localStorage.getItem('timecodePosition');
    return (saved as TimecodePosition) || 'bottomRight';
  });
  const [timecodeScale, setTimecodeScale] = useState<number>(() => {
    const saved = localStorage.getItem('timecodeScale');
    return saved ? parseFloat(saved) : 1.0; // 🔧 新增：預設 1.0
  });

  // 🔧 修復：監聽 localStorage 變化，自動同步位置和顯示狀態
  useEffect(() => {
    const handleLocalChange = () => {
      const newPosition = localStorage.getItem('timecodePosition');
      if (newPosition && newPosition !== timecodePosition) {
        console.log('🎯 useTimecodeDisplay: localStorage 變化，同步位置:', newPosition);
        setTimecodePosition(newPosition as TimecodePosition);
      }
      
      // 🔧 新增：監聽顯示開關變化
      const newShowTimecode = localStorage.getItem('showTimecode');
      if (newShowTimecode !== null) {
        const shouldShow = newShowTimecode !== 'false';
        if (shouldShow !== showTimecode) {
          console.log('🎯 useTimecodeDisplay: localStorage 變化，同步顯示:', shouldShow);
          setShowTimecode(shouldShow);
        }
      }
      
      // 🔧 新增：監聽背景開關變化
      const newShowBackground = localStorage.getItem('showTimecodeBackground');
      if (newShowBackground !== null) {
        const shouldShowBg = newShowBackground !== 'false';
        if (shouldShowBg !== showTimecodeBackground) {
          console.log('🎯 useTimecodeDisplay: localStorage 變化，同步背景:', shouldShowBg);
          setShowTimecodeBackground(shouldShowBg);
        }
      }
    };

    // 監聽自定義事件（由 SettingsMenu 觸發）
    window.addEventListener('localStorageChanged', handleLocalChange);

    return () => {
      window.removeEventListener('localStorageChanged', handleLocalChange);
    };
  }, [timecodePosition, showTimecode, showTimecodeBackground]);

  // ========== 時間碼更新用的 Refs（性能優化）==========
  const timecodeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timecodeRafIdRef = useRef<number | null>(null);
  const timecodeElRef = useRef<HTMLElement | null>(null);
  const lastKnownTimeRef = useRef<number>(0);
  const lastTickRef = useRef<number>(0);

  // ========== localStorage 同步 ==========
  useEffect(() => {
    localStorage.setItem('showTimecode', String(showTimecode));
  }, [showTimecode]);

  useEffect(() => {
    localStorage.setItem('showTimecodeBackground', String(showTimecodeBackground));
  }, [showTimecodeBackground]);

  useEffect(() => {
    localStorage.setItem('timecodePosition', timecodePosition);
  }, [timecodePosition]);

  useEffect(() => {
    localStorage.setItem('timecodeScale', String(timecodeScale));
  }, [timecodeScale]);

  // ========== 輔助函數 ==========
  
  /**
   * 更新時間碼（基於當前播放時間）
   */
  const updateTimecode = useCallback((time: number, frameRate: number = 30) => {
    const newTimecode = formatTime(time, true, frameRate);
    setCurrentTimecode(newTimecode);
    lastKnownTimeRef.current = time;
    lastTickRef.current = performance.now();
  }, []);

  /**
   * 重置時間碼為 00:00:00 00
   */
  const resetTimecode = useCallback(() => {
    setCurrentTimecode('00:00:00 00');
    lastKnownTimeRef.current = 0;
    lastTickRef.current = 0;
  }, []);

  // ========== 自訂 setter（帶 localStorage 同步）==========
  const setShowTimecodeWithStorage = useCallback((show: boolean) => {
    setShowTimecode(show);
    localStorage.setItem('showTimecode', String(show));
  }, []);

  const setShowTimecodeBackgroundWithStorage = useCallback((show: boolean) => {
    setShowTimecodeBackground(show);
    localStorage.setItem('showTimecodeBackground', String(show));
  }, []);

  const setTimecodePositionWithStorage = useCallback((position: TimecodePosition) => {
    console.log('🎯 setTimecodePosition 被调用:', position); // 🔧 調試
    setTimecodePosition(position);
    localStorage.setItem('timecodePosition', position);
  }, []);

  const setTimecodeScaleWithStorage = useCallback((scale: number) => {
    console.log('🎯 setTimecodeScale 被调用:', scale); // 🔧 調試
    setTimecodeScale(scale);
    localStorage.setItem('timecodeScale', String(scale));
  }, []);

  // ========== 清理 ==========
  useEffect(() => {
    return () => {
      // 清理所有計時器和動畫幀
      if (timecodeIntervalRef.current) {
        clearInterval(timecodeIntervalRef.current);
        timecodeIntervalRef.current = null;
      }
      if (timecodeRafIdRef.current) {
        cancelAnimationFrame(timecodeRafIdRef.current);
        timecodeRafIdRef.current = null;
      }
    };
  }, []);

  // ========== 返回 ==========
  return {
    // 狀態
    currentTimecode,
    showTimecode,
    showTimecodeBackground,
    timecodePosition,
    timecodeScale, // 🔧 新增

    // 操作函數（使用帶 localStorage 同步的版本）
    setCurrentTimecode,
    setShowTimecode: setShowTimecodeWithStorage,
    setShowTimecodeBackground: setShowTimecodeBackgroundWithStorage,
    setTimecodePosition: setTimecodePositionWithStorage,
    setTimecodeScale: setTimecodeScaleWithStorage, // 🔧 新增

    // 輔助函數
    updateTimecode,
    resetTimecode,
    
    // Refs
    timecodeIntervalRef,
    timecodeRafIdRef,
    timecodeElRef,
    lastKnownTimeRef,
    lastTickRef,
  };
};

export default useTimecodeDisplay;

